import React from 'react';
import styled from '@emotion/styled';
import type { BorderRadiusProps, BoxShadowProps, DisplayProps } from 'styled-system';
import { borderRadius, boxShadow, display } from 'styled-system';

import { Button } from 'common/components/Button';
import { useMatchesViewport } from 'common/hooks/media';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { PrimaryNavigationItemProps } from 'common/types/NavigationItem';
import { openSideNav, SideNavType } from 'common/utils/navigation';
import { buttonAriaAttributes, PrimaryNavigation } from './PrimaryNavigation';
import { trackCategoriesButtonClick } from './utils/tracking';

const Wrapper = styled.nav<BoxShadowProps>`
  ${boxShadow};
`;

const CategoriesButton = styled(Button)<DisplayProps & BorderRadiusProps>`
  ${display};
  ${borderRadius};
`;

const categoriesButtonStyles = {
  display: ['block', null, 'none'],
  borderRadius: 'x-large',
};

export interface TopNavigationBarProps {
  navigation?: PrimaryNavigationItemProps[];
  shopName?: string;

  // Render menu as button on S/M breakpoints
  asButtonOnMobile?: boolean;

  // Show box shadow below navigation
  withBoxShadow?: boolean;

  // Render right arrows next to items with children
  withArrows?: boolean;

  // Replace items which doesn't fit into container with single "Other" button
  wrapItems?: boolean;

  // Type of side navigation to be opened when clicked on menu item
  sideNavType?: SideNavType;
}

const TopNavigation = ({
  navigation = [],
  asButtonOnMobile = false,
  withBoxShadow = true,
  withArrows = false,
  wrapItems = false,
  sideNavType = SideNavType.Global,
  shopName,
}: TopNavigationBarProps) => {
  const { t } = useLocalization();
  const {
    matches: { matchesDesktop },
  } = useMatchesViewport();

  if (navigation.length === 0) {
    return null;
  }

  const ariaLabelKey =
    sideNavType === SideNavType.Global
      ? 'h24_top_navigation_label'
      : 'h24_top_navigation_secondary_label';

  const ariaHidden = sideNavType === SideNavType.Global && !matchesDesktop;

  return (
    <Wrapper
      aria-label={t(ariaLabelKey)}
      aria-hidden={ariaHidden}
      {...(withBoxShadow ? { boxShadow: 'bottom' } : {})}
    >
      {asButtonOnMobile && (
        <CategoriesButton
          kind="secondary"
          onClick={() => {
            openSideNav({ type: sideNavType });
            trackCategoriesButtonClick(t('h24_categories'), shopName);
          }}
          {...categoriesButtonStyles}
          {...buttonAriaAttributes}
        >
          {t('h24_categories')}
        </CategoriesButton>
      )}
      <PrimaryNavigation
        items={navigation}
        withArrows={withArrows}
        wrapItems={wrapItems}
        sideNavType={sideNavType}
        shopName={shopName}
      />
    </Wrapper>
  );
};
export default TopNavigation;
